import {useSnackBar} from "@hooks/alert/useSnackBar";
import {buildErrorsMessages} from "@ui/errors/helpers/helpers";
import {useMutation} from "@tanstack/react-query";
import {useTranslation} from "react-i18next";
import {
    IPropsStartSingleVoyage,
    IPropsStopSingleVoyage,
    IResultStartSingleVoyage,
    IResultStopSingleVoyage
} from "@containers/singleVoyage/actionsSingleVoyage/types";
import {
    archiveSingleVoyageApi,
    deleteSingleVoyage,
    saveSingleVoyage,
    startSingleVoyageApi,
    stopSingleVoyageApi,
    updatePlannedResourcesSingleVoyage
} from "@containers/singleVoyage/actionsSingleVoyage/api";
import {refetchSingleVoyages} from "@containers/singleVoyage/refetchSingleVoyages/refetchSingleVoyages";
import {
    getDeviceErrorLabelKey
} from "@containers/singleVoyage/actionsSingleVoyage/helpers/isDeviceBusyError";
import {ISingleVoyage} from "@containers/singleVoyage/types/voyage";
import {getCloseStopsErrorMessage} from "@containers/singleVoyage/containers/closeStops/closeStopsMessages";


export const useStartSingleVoyageQuery = ({
                                              onSuccess
                                          }: {
    onSuccess?: (result: IResultStartSingleVoyage) => void
} = {}) => {
    const {setSnackBar} = useSnackBar()

    const {t} = useTranslation()

    return useMutation({
        mutationFn: startSingleVoyageApi,
        onSuccess: (res) => {
            refetchSingleVoyages()
            setSnackBar({
                title: t('singleVoyage.successStartVoyage'),
                variant: 'info'
            })
            onSuccess && onSuccess(res)
        },
        onError: (e) => {
            // the raw backend text is English and carries internal ids, so the errors a
            // dispatcher actually hits here get a readable message
            const deviceErrorLabelKey = getDeviceErrorLabelKey(e)
            setSnackBar({
                message: deviceErrorLabelKey ? t(deviceErrorLabelKey) : buildErrorsMessages(e),
                variant: 'error'
            })
        }
    });
}


export const useArchiveSingleVoyageQuery = ({onSuccess}: {
    onSuccess?: () => void
} = {}) => {
    const {setSnackBar} = useSnackBar()

    const {t} = useTranslation()

    return useMutation({
        mutationFn: archiveSingleVoyageApi,
        onSuccess: () => {
            refetchSingleVoyages()
            setSnackBar({
                title: t('singleVoyage.successArchiveVoyage'),
                variant: 'info'
            })
            onSuccess && onSuccess()
        },
        onError: (e) => {
            setSnackBar({
                message: buildErrorsMessages(e),
                variant: 'error'
            })
        }
    });
}

export const useStopSingleVoyageQuery = ({
                                             onSuccess
                                         }: {
    onSuccess?: (result: IResultStopSingleVoyage) => void
} = {}) => {
    const {setSnackBar} = useSnackBar()

    const {t} = useTranslation()

    return useMutation({
        mutationFn: stopSingleVoyageApi,
        onSuccess: (res) => {
            refetchSingleVoyages()
            setSnackBar({
                title: t('singleVoyage.successStopVoyage'),
                variant: 'info'
            })
            onSuccess && onSuccess(res)
        },
        onError: (e) => {
            const message = buildErrorsMessages(e)
            setSnackBar({
                message,
                variant: 'error'
            })
        }
    });
}

export const useDeleteSingleVoyageQuery = ({onSuccess}: {
    onSuccess?: () => void
} = {}) => {
    const {setSnackBar} = useSnackBar()

    const {t} = useTranslation()

    return useMutation({
        mutationFn: deleteSingleVoyage,
        onSuccess: () => {
            refetchSingleVoyages()
            setSnackBar({
                title: t('successDeleteMessage'),
                variant: 'info'
            })
            onSuccess && onSuccess()
        },
        onError: (e) => {
            const message = buildErrorsMessages(e)
            setSnackBar({
                message,
                variant: 'error'
            })
        }
    });


}

export const useSaveVoyageQuery = ({
                                       onSuccess,
                                       disableRefetch,
                                   }: {
    onSuccess?: (singleVoyage: ISingleVoyage) => void,
    disableRefetch?: boolean
} = {}) => {
    const {setSnackBar} = useSnackBar()

    const {t} = useTranslation()

    return useMutation({
        mutationFn: saveSingleVoyage,
        onSuccess: (res) => {
            if (!disableRefetch) {
                refetchSingleVoyages()
            }
            setSnackBar({
                title: t('base.successSaveMessage'),
                variant: 'info'
            })
            onSuccess && onSuccess(res)
        },
        onError: (e) => {
            const deviceErrorLabelKey = getDeviceErrorLabelKey(e)
            setSnackBar({
                message: deviceErrorLabelKey ? t(deviceErrorLabelKey) : getCloseStopsErrorMessage(e) ?? buildErrorsMessages(e),
                variant: 'error'
            })
        }
    });
}


export const useUpdatePlannedResourcesSingleVoyageQuery = ({
                                                               onSuccess
                                                           }: {
    onSuccess?: (singleVoyage: ISingleVoyage) => void
} = {}) => {
    const {setSnackBar} = useSnackBar()

    const {t} = useTranslation()

    return useMutation({
        mutationFn: updatePlannedResourcesSingleVoyage,
        onSuccess: (res) => {
            refetchSingleVoyages()
            setSnackBar({
                title: t('base.successSaveMessage'),
                variant: 'info'
            })
            onSuccess && onSuccess(res)
        },
        onError: (e) => {
            // this endpoint validates the device since the booking rules were unified, so it can
            // now fail the same way creating or starting a voyage does
            const deviceErrorLabelKey = getDeviceErrorLabelKey(e)
            setSnackBar({
                message: deviceErrorLabelKey ? t(deviceErrorLabelKey) : buildErrorsMessages(e),
                variant: 'error'
            })
        }
    });
}